/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import axios from "axios"
import { ALL_ORDERS, API_DEFAULT_LIMIT } from "common/constants/misc"
import { ordersConvertor } from "common/duck/convertors"
import { Order } from "common/types/models/order"
import { TroyFoodPortalEndpoint } from "constants/endpoints"
import includes from "ramda/src/includes"
import { Epic, ofType } from "redux-observable"
import { concat, of } from "rxjs"
import { delay, mapTo, switchMap } from "rxjs/operators"
import { AppState, EpicDependenciesType } from "store/store.type"
import { createReducer } from "../create-reducer"
import { actions as MerchantActionTypes } from "./merchant"
import { Orders } from "./orders.type"

const NAMESPACE = "ORDERS"

// actions
const RESET_ORDERS = `${NAMESPACE}/RESET_ORDERS`
const FETCH_ORDERS_NEXT = `${NAMESPACE}/FETCH_ORDERS_NEXT`
const FETCH_ORDERS_NEXT_SUCCESS = `${NAMESPACE}/FETCH_ORDERS_NEXT_SUCCESS`
const FETCH_ORDERS_FAILED = `${NAMESPACE}/FETCH_ORDERS_FAILED`
const CANCEL_PENDING_REQUESTS = `${NAMESPACE}/CANCEL_PENDING_REQUESTS`

export const actions = {
  RESET_ORDERS,
  FETCH_ORDERS_NEXT,
  FETCH_ORDERS_NEXT_SUCCESS,
  FETCH_ORDERS_FAILED,
  CANCEL_PENDING_REQUESTS,
}

type ActionTypes =
  | typeof RESET_ORDERS
  | typeof FETCH_ORDERS_NEXT
  | typeof FETCH_ORDERS_NEXT_SUCCESS
  | typeof FETCH_ORDERS_FAILED
  | typeof CANCEL_PENDING_REQUESTS

// action creators
const resetOrders = () => ({
  type: RESET_ORDERS,
})
const fetchOrdersNext = ({
  queryID,
  longOrderID,
  startDate,
  endDate,
}: {
  queryID: StoreID
  longOrderID?: LongOrderID
  startDate: string
  endDate: string
}) => {
  const payload = {
    queryID,
    longOrderID,
    startDate,
    endDate,
  }
  if (longOrderID === ALL_ORDERS) {
    payload.longOrderID = undefined
  }
  return {
    type: FETCH_ORDERS_NEXT,
    payload,
  }
}
const fetchOrdersNextSuccess = (
  orders: Array<Order>,
  dataReady: boolean,
  nextID?: SinceID,
) => ({
  type: FETCH_ORDERS_NEXT_SUCCESS,
  payload: {
    orders,
    dataReady,
    nextID,
  },
})
const fetchOrdersFailed = (error: string) => ({
  type: FETCH_ORDERS_FAILED,
  payload: {
    error,
  },
})
const cancelPendingRequests = () => ({
  type: CANCEL_PENDING_REQUESTS,
})
export const actionCreator = {
  resetOrders,
  fetchOrdersNext,
  fetchOrdersNextSuccess,
  fetchOrdersFailed,
  cancelPendingRequests,
}

type ActionCreatorTypes = {
  [K in keyof typeof actionCreator]: ReturnType<typeof actionCreator[K]>
}
export type ActionCreatorTypeList = ActionCreatorTypes[keyof ActionCreatorTypes]

// state
export const initialState = (): Orders => ({
  error: null,
  fetching: false,
  orders: [],
  orderTypes: [],
  orderStatus: [],
  payMethods: [],
  dataReady: false,
  cancelTokenSource: axios.CancelToken.source(),
  prevID: null,
  middleID: 0,
  nextID: 0,
})

// reducer
export const reducer = createReducer<
  Orders,
  ActionTypes,
  ActionCreatorTypeList
>(initialState(), {
  [RESET_ORDERS]: () => {
    return initialState()
  },
  [FETCH_ORDERS_NEXT]: (state: Orders) => ({
    ...state,
    fetching: true,
  }),
  [FETCH_ORDERS_NEXT_SUCCESS]: (
    state: Orders,
    action: ActionCreatorTypes["fetchOrdersNextSuccess"],
  ) => {
    const { orders: payloadOrders, dataReady, nextID = null } = action.payload
    const { orders, orderTypes, orderStatus, payMethods } = state
    payloadOrders.forEach(order => {
      const { status, orderType, payMethod } = order
      orders.push(order)
      if (!includes(orderType, orderTypes)) {
        orderTypes.push(orderType)
        orderTypes.sort()
      }
      if (!includes(status, orderStatus)) {
        orderStatus.push(status)
        orderStatus.sort()
      }
      if (!includes(payMethod, payMethods)) {
        payMethods.push(payMethod)
        payMethods.sort()
      }
    })
    return {
      ...state,
      fetching: !dataReady,
      dataReady,
      prevID: state.middleID,
      middleID: state.nextID,
      nextID,
    }
  },
  [FETCH_ORDERS_FAILED]: (
    state: Orders,
    action: ActionCreatorTypes["fetchOrdersFailed"],
  ) => {
    return {
      ...state,
      error: action.payload.error,
      dataReady: false,
      fetching: false,
    }
  },
})

// epic
const propagateToResetOrdersEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList
> = action$ =>
  action$.pipe(ofType(MerchantActionTypes.RESET_DETAIL), mapTo(resetOrders()))
const cancelPendingRequestsEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState
> = (action$, store$) =>
  action$.pipe(
    ofType(CANCEL_PENDING_REQUESTS),
    switchMap(() => {
      store$.value.orders.cancelTokenSource.cancel()
      return of(resetOrders()).pipe(delay(100))
    }),
  )
const fetchOrdersEpic: Epic<
  ActionCreatorTypeList,
  ActionCreatorTypeList,
  AppState,
  EpicDependenciesType
> = (action$, store$, { httpRequest }) =>
  action$.pipe(
    ofType(FETCH_ORDERS_NEXT),
    switchMap((action: ActionCreatorTypes["fetchOrdersNext"]) => {
      const { queryID, longOrderID, startDate, endDate } = action.payload
      const cancelTokenSource = store$.value.orders.cancelTokenSource
      const nextID = store$.value.orders.nextID
      const params = {
        merchantID: queryID,
        startTime: startDate,
        endTime: endDate,
        limit: API_DEFAULT_LIMIT,
        orderID: longOrderID,
        sinceID: nextID!,
      }
      return httpRequest
        .GET<APIGetOrdersResp>({
          endpoint: TroyFoodPortalEndpoint.getOrdersV1,
          params,
          cancelToken: cancelTokenSource.token,
        })
        .pipe(
          switchMap(res => {
            if (res.data === null) {
              return of(fetchOrdersFailed("error"))
            }
            const orders = ordersConvertor(res.data.orders)
            const sinceID = res.data.sinceID
            const dataReady = sinceID === undefined || sinceID === 0
            if (dataReady) {
              return of(fetchOrdersNextSuccess(orders, dataReady))
            }
            return concat(
              of(fetchOrdersNextSuccess(orders, dataReady, sinceID)),
              of(
                fetchOrdersNext({
                  queryID,
                  longOrderID,
                  startDate,
                  endDate,
                }),
              ),
            )
          }),
        )
    }),
  )
export const epics = {
  propagateToResetOrdersEpic,
  fetchOrdersEpic,
  cancelPendingRequestsEpic,
}
